import clsx from 'clsx';
import { useConditionalFeature } from '@americanexpress/conditional-features';
import { getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { useSelector } from 'react-redux';
import { MarketingTilesArticles } from './MarketingTilesArticles';
import { getSelectedAccount } from '../../utils/travelerDataUtils';
import TravelBenefits from '../TravelBenefits/TravelBenefits';
import LargeTile from '../CardOffersTiles/LargeTile';
import { getSelectedKeySelector } from '../../selectors/getSelectedKeySelector';
import { hasMyTravelBenefitsRule } from '../../constants/featureRulesConstants';
import type { CardType, MarketingTilesCard } from '../../types/components';

type MarketingTilesProps = {
  personalizedOffersData: {
    cards: Omit<MarketingTilesCard, 'id' | 'analyticElement'>[];
    carousel: Array<{}>;
    benefits: Array<{}>;
  };
  cardType?: CardType;
  hasTravelerError: boolean;
  travelerData: {
    card?: {
      type?: string;
    };
    loyalty?: Record<string, unknown>;
  };
};

export const MarketingTiles = ({
  personalizedOffersData,
  cardType = 'guest',
  hasTravelerError,
  travelerData,
}: MarketingTilesProps) => {
  const {
    cards: personalizedOffersCards,
    carousel: personalizedOffersCarousel,
    benefits: personalizedOffersBenefits
  } = personalizedOffersData || {};
  const hasBenefitsData = !!personalizedOffersData && Array.isArray(personalizedOffersBenefits);
  const cards = Array.isArray(personalizedOffersCards) ? personalizedOffersCards : [];
  const carousel = Array.isArray(personalizedOffersCarousel) ? personalizedOffersCarousel : [];
  const benefits = hasBenefitsData ? personalizedOffersBenefits : [];
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const hasMyTravelBenefits = useConditionalFeature(hasMyTravelBenefitsRule);

  const showTravelBenefits = isLoggedIn
    && hasMyTravelBenefits
    && !hasTravelerError
    && travelerData
    && hasBenefitsData;

  const selectedKey = useSelector(getSelectedKeySelector);
  const selectedAccountData = getSelectedAccount(travelerData, selectedKey);

  return (
    <section className="width-full flex flex-column items-center gap-4 px-4 py-8">
      <div className="row">
        {
          showTravelBenefits
          && (
            <div className={clsx(
              'col-lg-4 col-md-6 margin-4-t margin-4-b pad-2-lr-sm-down pad-3-l-md-up pad-1-r-lg-up pad-2-l-lg-up',
              )}>
              <TravelBenefits
                travelBenefits={benefits}
                travelerData={travelerData}
                selectedKey={selectedKey}
                selectedAccountData={selectedAccountData}
              />
            </div>
          )
        }
        <div className={clsx(
          'margin-4-b pad-2-r-lg-up',
          showTravelBenefits
            ? 'col-lg-8 col-md-6 margin-4-t-md-up pad-0-l pad-0-r-sm-down pad-3-r-md-up'
            : 'col margin-3-t-lg-up pad-2-lr-sm-down pad-3-lr-md-up pad-2-l-lg-up')}
        >
          <LargeTile
            showTravelBenefits={showTravelBenefits}
            carousel={carousel}
            selectedAccountData={selectedAccountData}
          />
        </div>
      </div>
      <MarketingTilesArticles
        cards={cards?.map((card, index) => ({
          id: `${(card?.heading || '').toLowerCase().replace(/\s/g, '-')}-${index}`,
          heading: card?.heading,
          imageLocationLabel: card?.imageLocationLabel,
          imageUrl: card?.imageUrl,
          subHeading: card?.subHeading || '',
          ctaLabel: card?.ctaLabel,
          redirectUrl: card?.redirectUrl,
        }))}
        cardType={cardType}
      />
    </section>
  );
};
